import { yyyymmdddash } from '@/services/utils';
import { useRevenueAllocationStore } from '@/stores/useRevenueAllocationStore';
export default function useChargePreparer() {
  // Local mapping for allocation modes to avoid store dependency issues
  const ALLOCATION_MODE_VALUES = {
    0: 'Percentage',
    1: 'WorkedHour',
    2: 'WorkedAmount'
  };
  const prepareChargeData = (charge, dates, formData, recurrence, selectedService, selectedStandard, features) => {
    const revenueAllocationStore = useRevenueAllocationStore();
    const chargeData = {
      id: charge.id,
      amount: charge.amount,
      description: charge.description,
      label: charge.label,
      chargeType: charge.chargeType,
      completed: charge.completed,
      billed: charge.billed,
      billedState: charge.billedState,
      customFlag: charge.customFlag,
      accounting: {},
      allocation: {},
      date: dates.start ? yyyymmdddash(dates.start) : '',
      recurrence: {}
    };

    // Only include fixedFeeAllocationMode if feature flag is enabled
    // Convert numeric mode to string representation for API
    if (features.fixedFeeAllocation) {
      chargeData.fixedFeeAllocationMode = ALLOCATION_MODE_VALUES[charge.allocationMode];
    }

    // Handle matter - check cm.matter first (used by ClientMatterSearch component)
    // then fall back to charge.matter for backwards compatibility
    if (charge.cm && charge.cm.matter && charge.cm.matter.id) {
      chargeData.matter = {
        id: charge.cm.matter.id
      };
    } else if (charge.matter && charge.matter.id) {
      chargeData.matter = {
        id: charge.matter.id
      };
    }

    // Handle codes - ensure they are properly formatted
    if (charge.codes) {
      chargeData.codes = {
        activity: charge.codes.activity && charge.codes.activity.id ? {
          id: charge.codes.activity.id
        } : {},
        task: charge.codes.task && charge.codes.task.id ? {
          id: charge.codes.task.id
        } : {}
      };
    }

    // Handle account
    if (charge.account) {
      chargeData.account = {
        id: charge.account.id,
        name: charge.account.name,
        first: charge.account.first,
        last: charge.account.last,
        initials: charge.account.initials
      };
    }

    // Handle limit
    if (charge.limit && charge.limit.hours) {
      chargeData.limit = {
        hours: charge.limit.hours,
        rate: {
          amount: charge.limit.rate && charge.limit.rate.amount
        }
      };
    } else {
      chargeData.limit = null;
    }

    // Handle recurrence
    if (recurrence) {
      const periodValue = recurrence.id || recurrence;
      // EndDate and Count are a mutually exclusive OR in the UI — if an EndDate is
      // present, force count to null defensively so an out-of-sync formData.count
      // can never be sent alongside it.
      const countValue = dates.end ? null : formData.count === '' || formData.count == null ? null : Number(formData.count);
      chargeData.recurrence = {
        period: String(periodValue),
        count: countValue
      };
      if (dates.end) {
        chargeData.recurrence.endDate = yyyymmdddash(dates.end);
      }
      chargeData.recurrence.includeDateInDescription = Boolean(formData.includeDate);
    } else {
      delete chargeData.recurrence;
    }

    // Handle allocations
    // For NEW UI (with fixedFeeAllocation feature): only include for Percentage mode
    // For OLD UI (without feature flag): always include allocations if they exist
    if (formData.allocations && formData.allocations.length) {
      // If feature flag is disabled (OLD UI), always include allocations
      // If feature flag is enabled (NEW UI), only include for Percentage mode
      const shouldIncludeAllocations = !features.fixedFeeAllocation || charge.allocationMode === revenueAllocationStore.ALLOCATION_MODES.PERCENTAGE;
      if (shouldIncludeAllocations) {
        chargeData.allocation = {
          accounts: formData.allocations.map(a => {
            const accountData = {
              id: a.account.id,
              amount: a.amount
            };
            // Only include percentage when fixedFeeAllocation feature is enabled
            if (features.fixedFeeAllocation) {
              accountData.percentage = a.percentage;
            }
            return accountData;
          })
        };
      } else {
        // Ensure allocation is cleared for non-Percentage modes in NEW UI
        chargeData.allocation = {};
      }
    } else {
      chargeData.allocation = {};
    }

    // Handle QuickBooks mapping
    if (features && features.qboFixedFeeAndExpenseMapping) {
      chargeData.accounting = {
        service: selectedService || selectedStandard && selectedStandard.accounting && selectedStandard.accounting.service
      };
    }
    return chargeData;
  };
  return {
    prepareChargeData
  };
}